import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import {
  GetWorkspaceUsersDocument,
  connectionRows,
  type WorkspaceUserRow,
} from "#app/lib/data";
import { memberStatusPrecedence } from "#app/lib/role-utils";

/**
 * Workspace members by lowercased email, asked of the server — for addresses
 * the bootstrap roster (one page) cannot answer. A member's stored address
 * keeps the case it was invited or edited with, so the match is
 * case-insensitive on the server and keyed lowercased here. `pending` holds while the
 * answer for the current addresses has not landed; the previous answer
 * stands until then, so a grown address list does not drop known rows.
 */
export function useMembersByEmail(emails: readonly string[]): {
  byEmail: Map<string, WorkspaceUserRow>;
  pending: boolean;
} {
  const variables = useMemo(
    () => ({
      where: { email: { in: [...emails], mode: "insensitive" } },
      limit: emails.length,
    }),
    [emails],
  );
  const { data, previousData, loading } = useQuery(GetWorkspaceUsersDocument, {
    variables,
    skip: emails.length === 0,
  });
  const byEmail = useMemo(() => {
    const map = new Map<string, WorkspaceUserRow>();
    for (const row of connectionRows<WorkspaceUserRow>((data ?? previousData)?.users)) {
      if (!row.email) continue;
      const key = row.email.toLowerCase();
      const held = map.get(key);
      if (
        !held ||
        memberStatusPrecedence(row.memberStatus) < memberStatusPrecedence(held.memberStatus)
      ) {
        map.set(key, row);
      }
    }
    return map;
  }, [data, previousData]);
  return { byEmail, pending: loading };
}
